/**
 * 自定义提供商配置页（T15）。
 *
 * 路由 `#/providers`，由 `src/app/router.tsx` 接线；不占用底部导航，也不改既有
 * 四条主路由的行为。
 *
 * 能力：
 * - 列表：展示 providerRepo 全部配置（类型 / 名称 / 基址 / 默认模型 / 启用开关），
 *   支持新增 / 编辑 / 删除（二次确认）/ 启停；
 * - 表单：kind / name / baseUrl（URL 校验）/ apiKey / defaultModel / enabled，
 *   kind 与 baseUrl placeholder 联动但不覆盖用户输入，apiKey 默认打码可临时显形；
 * - 连通性测试：经 createProviderAdapter().listModels() 探测，不落库；
 * - 每个可见部件都通过 `useComponentMeta` 登记进 `src/ui/registry.tsx`（390x844 逻辑坐标）。
 *
 * 动效：页面切换 pageTransition、按压 tap、列表 listStagger，全部取自 `src/design/motion.ts`。
 */
import { AnimatePresence, motion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import type { AriaRole, CSSProperties, ReactElement, ReactNode, RefObject } from 'react';

import { newId } from '../../core/ids';
import type { ProviderConfig, ResolvedProviderConfig } from '../../core/providers';
import { providerPurpose } from '../../core/providers';
import {
  deleteProvider,
  listProviders,
  putProvider,
  updateProvider,
} from '../../core/repositories/providerRepo';
import type { ComponentMeta } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import type { PageDirection } from '../../design/motion';
import { AppIcon, buildComponentMeta, useComponentMeta } from '../../ui';
import { ImageCropDialog } from '../../ui/ImageCropDialog';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import {
  PAGE_ID,
  PROVIDER_ITEM_HEIGHT,
  PROVIDER_LIST_LAYOUT,
  providerItemLayout,
  providerItemPartLayout,
  providerMetaById,
} from './componentMeta';
import type { ProviderItemPart } from './componentMeta';
import { testProviderConnection } from './connection';
import {
  KIND_BASE_URL_HINT,
  KIND_LABEL,
  PROVIDER_PURPOSE_LABEL,
  createProviderDraft,
  draftToProviderConfig,
  hasDraftErrors,
  providerConfigToDraft,
  supportsImagePurpose,
  supportsTtsPurpose,
  validateProviderDraft,
} from './draft';
import type { ProviderDraft, ProviderDraftErrors } from './draft';
import type { NormalizedProviderError } from './errors';
import { contentBoxStyle, offsetBoxStyle } from './layout';
import styles from './providers.module.css';
import { hasSecret, readSecret, removeSecret, writeSecret } from './secretStore';
import { applyVendor, draftProtocol, EMPTY_VENDOR_LOCKS, fetchProviderVendors, PROTOCOL_LABELS, protocolKind } from './vendors';
import type { ProviderProtocol, ProviderVendor, VendorField } from './vendors';
import { resizeVendorLogo } from './vendorLogo';

const MODEL_DATALIST_ID = 'providers-model-options';

type ComponentRole = NonNullable<ComponentMetaInput['role']>;
type ViewMode = 'list' | 'form';
interface ProviderListRow {
  key: string;
  provider: ProviderConfig | null;
  vendor: ProviderVendor | null;
}

type TestState =
  | { status: 'idle' }
  | { status: 'testing' }
  | { status: 'success'; models: string[] }
  | { status: 'error'; error: NormalizedProviderError };

/** 按 id 取静态部件元数据（渲染与清单同源）。 */
function m(id: string): ComponentMetaInput {
  return providerMetaById(id);
}

interface Registered<T extends HTMLElement> {
  componentMeta: ComponentMeta;
  ref: RefObject<T>;
  style: CSSProperties;
}

/** 登记 + 计算绝对定位样式（parent 用于列表项内部部件）。 */
function useRegistered<T extends HTMLElement>(
  meta: ComponentMetaInput,
  fallbackRole: ComponentRole = 'container',
  parent?: BoxLayout,
): Registered<T> {
  const componentMeta = buildComponentMeta(meta, fallbackRole);
  const ref = useComponentMeta<T>(componentMeta);
  const style =
    parent === undefined ? contentBoxStyle(meta.layout) : offsetBoxStyle(meta.layout, parent);
  return { componentMeta, ref, style };
}

interface RegBlockProps {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  className?: string;
  children?: ReactNode;
  ariaRole?: AriaRole;
  ariaModal?: boolean;
  ariaHidden?: boolean;
  surface?: 'page' | 'panel' | 'control' | 'overlay';
}

function RegBlock({
  meta,
  parent,
  className,
  children,
  ariaRole,
  ariaModal,
  ariaHidden,
  surface,
}: RegBlockProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLDivElement>(meta, 'container', parent);
  return (
    <div
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      role={ariaRole}
      aria-modal={ariaModal === true ? true : undefined}
      aria-label={ariaRole === undefined ? undefined : componentMeta.label}
      aria-hidden={ariaHidden}
      data-skin-surface={surface}
      style={style}
    >
      {children}
    </div>
  );
}

interface RegTextProps {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  className?: string;
  children: ReactNode;
}

function RegHeading({ meta, className, children }: RegTextProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLHeadingElement>(meta, 'text');
  return (
    <h1
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
    >
      {children}
    </h1>
  );
}

function RegText({ meta, parent, className, children }: RegTextProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLParagraphElement>(meta, 'text', parent);
  const merged = className === undefined ? styles.text : `${styles.text} ${className}`;
  return (
    <p
      ref={ref}
      className={merged}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
    >
      {children}
    </p>
  );
}

interface RegButtonProps extends Omit<HTMLMotionProps<'button'>, 'style' | 'children' | 'ref'> {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  children: ReactNode;
}

function RegButton({
  meta,
  parent,
  children,
  className,
  type = 'button',
  ...rest
}: RegButtonProps): ReactElement {
  const presets = useMotionPresets();
  const { componentMeta, ref, style } = useRegistered<HTMLButtonElement>(meta, 'button', parent);
  return (
    <motion.button
      {...rest}
      ref={ref}
      type={type}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      style={style}
      whileTap={presets.tap.whileTap}
      transition={presets.tap.transition}
    >
      {children}
    </motion.button>
  );
}

interface RegSwitchProps {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  className?: string;
  checked: boolean;
  onToggle: () => void;
}

function RegSwitch({
  meta,
  parent,
  className,
  checked,
  onToggle,
}: RegSwitchProps): ReactElement {
  const presets = useMotionPresets();
  const { componentMeta, ref, style } = useRegistered<HTMLButtonElement>(meta, 'button', parent);
  return (
    <motion.button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={componentMeta.label}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
      whileTap={presets.tap.whileTap}
      transition={presets.tap.transition}
      onClick={onToggle}
    >
      <span className={styles.toggleKnob} />
    </motion.button>
  );
}

interface RegInputProps {
  meta: ComponentMetaInput;
  className?: string;
  type?: 'text' | 'password';
  value: string;
  placeholder?: string;
  listId?: string;
  autoComplete?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}

function RegInput({
  meta,
  className,
  type = 'text',
  value,
  placeholder,
  listId,
  autoComplete,
  disabled,
  onChange,
}: RegInputProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLInputElement>(meta, 'input');
  return (
    <input
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      type={type}
      value={value}
      placeholder={placeholder}
      list={listId}
      autoComplete={autoComplete}
      disabled={disabled}
      aria-label={componentMeta.label}
      onChange={(event) => onChange(event.currentTarget.value)}
      style={style}
    />
  );
}

interface RegSelectProps {
  meta: ComponentMetaInput;
  className?: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  disabled?: boolean;
}

function RegSelect({
  meta,
  className,
  value,
  onChange,
  children,
  disabled,
}: RegSelectProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLSelectElement>(meta, 'input');
  return (
    <select
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.value)}
      style={style}
    >
      {children}
    </select>
  );
}

interface RegListProps {
  meta: ComponentMetaInput;
  className?: string;
  children?: ReactNode;
}

function RegList({ meta, className, children }: RegListProps): ReactElement {
  const presets = useMotionPresets();
  const { componentMeta, ref, style } = useRegistered<HTMLDivElement>(meta, 'list');
  return (
    <motion.div
      ref={ref}
      role="list"
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
      variants={presets.listStagger.container}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.div>
  );
}

interface RegCardProps {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  children: ReactNode;
}

function RegCard({ meta, parent, children }: RegCardProps): ReactElement {
  const presets = useMotionPresets();
  const { componentMeta, ref, style } = useRegistered<HTMLDivElement>(meta, 'container', parent);
  return (
    <motion.div
      ref={ref}
      className={styles.item}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      data-skin-surface="panel"
      style={style}
      variants={presets.listStagger.item}
    >
      {children}
    </motion.div>
  );
}

interface ProviderRowProps {
  provider: ProviderConfig;
  index: number;
  onToggle: (provider: ProviderConfig) => void;
  onEdit: (provider: ProviderConfig) => void;
  onDelete: (provider: ProviderConfig) => void;
}

function ProviderRow({
  provider,
  index,
  onToggle,
  onEdit,
  onDelete,
}: ProviderRowProps): ReactElement {
  const itemLayout = providerItemLayout(index);
  const itemId = `providers.item.${provider.id}`;

  const cardMeta: ComponentMetaInput = {
    id: itemId,
    page: PAGE_ID,
    role: 'container',
    label: `提供商：${provider.name}`,
    layout: itemLayout,
    zIndex: 4,
    props: ['kind', 'purpose', 'name', 'baseUrl', 'defaultModel', 'enabled'],
    notes: '运行时列表项；安卓用 RecyclerView item',
  };

  const partMeta = (
    part: ProviderItemPart,
    label: string,
    role: ComponentRole,
    partProps?: string[],
  ): ComponentMetaInput => ({
    id: `${itemId}.${part}`,
    page: PAGE_ID,
    role,
    label,
    layout: providerItemPartLayout(itemLayout, part),
    zIndex: 5,
    ...(partProps === undefined ? {} : { props: partProps }),
    notes: '列表项内部部件',
  });

  const secretState = hasSecret(provider.apiKeyRef) ? '已配置密钥' : '未配置密钥';

  return (
    <RegCard meta={cardMeta} parent={PROVIDER_LIST_LAYOUT}>
      <RegText meta={partMeta('name', '提供商名称', 'text')} parent={itemLayout} className={styles.itemName}>
        {provider.logo && <img className={styles.itemLogo} src={provider.logo} alt="" referrerPolicy="no-referrer" />}{provider.name}
      </RegText>
      <RegText meta={partMeta('kind', '提供商类型', 'text')} parent={itemLayout} className={styles.itemMeta}>
        {`${provider.protocol ? PROTOCOL_LABELS[provider.protocol] : KIND_LABEL[provider.kind]} · ${PROVIDER_PURPOSE_LABEL[providerPurpose(provider)]} · ${secretState}`}
      </RegText>
      <RegText meta={partMeta('baseUrl', '提供商基址', 'text')} parent={itemLayout} className={styles.itemMeta}>
        {provider.baseUrl}
      </RegText>
      <RegText
        meta={partMeta('defaultModel', '默认模型', 'text')}
        parent={itemLayout}
        className={styles.itemMeta}
      >
        {provider.defaultModel ? `默认模型：${provider.defaultModel}` : '未选择默认模型'}
      </RegText>
      <RegSwitch
        meta={partMeta('enabledToggle', '启用或停用', 'button', ['checked'])}
        parent={itemLayout}
        className={provider.enabled ? `${styles.toggle} ${styles.toggleOn}` : styles.toggle}
        checked={provider.enabled}
        onToggle={() => onToggle(provider)}
      />
      <RegButton
        meta={partMeta('editButton', '编辑提供商', 'button', ['pressed'])}
        parent={itemLayout}
        className={styles.iconButton}
        onClick={() => onEdit(provider)}
      >
        ✏️
      </RegButton>
      <RegButton
        meta={partMeta('deleteButton', '删除提供商', 'button', ['pressed'])}
        parent={itemLayout}
        className={styles.iconButtonDanger}
        onClick={() => onDelete(provider)}
      >
        🗑
      </RegButton>
    </RegCard>
  );
}

function describeTestResult(state: TestState): string {
  if (state.status === 'testing') return '正在测试连接…';
  if (state.status === 'success') {
    if (state.models.length === 0) return '连接成功，但服务端未返回任何模型';
    const preview = state.models.slice(0, 3).join('、');
    const suffix = state.models.length > 3 ? ' …' : '';
    return `连接成功，共 ${state.models.length} 个模型：${preview}${suffix}`;
  }
  if (state.status === 'error') return `测试失败（${state.error.code}）：${state.error.message}`;
  return '尚未测试连接';
}

export function ProvidersPage(): ReactElement {
  const presets = useMotionPresets();
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState<ViewMode>('list');
  const [direction, setDirection] = useState<PageDirection>('forward');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProviderDraft>(() => createProviderDraft());
  const [errors, setErrors] = useState<ProviderDraftErrors>({});
  const [models, setModels] = useState<string[]>([]);
  const [test, setTest] = useState<TestState>({ status: 'idle' });
  const [revealApiKey, setRevealApiKey] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProviderConfig | null>(null);
  const [vendors, setVendors] = useState<ProviderVendor[]>([]);
  const [vendorStatus, setVendorStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [vendorRevision, setVendorRevision] = useState(0);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const vendor = vendors.find((item) => item.id === draft.vendorId);
  const locks = vendor?.locks ?? (draft.vendorId ? { name: true, protocol: true, baseUrl: true, purpose: true, logo: true } : EMPTY_VENDOR_LOCKS);
  const protocol = draftProtocol(draft);

  useEffect(() => {
    const abort = new AbortController();
    setVendorStatus('loading');
    void fetchProviderVendors(abort.signal).then((rows) => {
      if (abort.signal.aborted) return;
      setVendors(rows); setVendorStatus('ready');
      setDraft((previous) => {
        const current = rows.find((item) => item.id === previous.vendorId);
        return current ? applyVendor(previous, current, true) : previous;
      });
    }).catch(() => { if (!abort.signal.aborted) setVendorStatus('error'); });
    return () => abort.abort();
  }, [vendorRevision]);

  const refresh = useCallback(async (): Promise<void> => {
    const rows = await listProviders();
    setProviders(rows);
    setLoaded(true);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updateDraft = (patch: Partial<ProviderDraft>): void => {
    const allowed = { ...patch };
    for (const field of Object.keys(EMPTY_VENDOR_LOCKS) as VendorField[]) {
      if (locks[field]) Reflect.deleteProperty(allowed, field);
    }
    setDraft((prev) => ({ ...prev, ...allowed }));
    if (['kind', 'protocol', 'baseUrl', 'apiKey', 'purpose'].some((key) => Object.hasOwn(allowed, key))) {
      setModels([]); setTest({ status: 'idle' });
    }
    setFormError('');
  };

  const openCreate = (selected?: ProviderVendor): void => {
    setDirection('forward');
    setEditingId(null);
    setDraft(selected ? applyVendor(createProviderDraft(), selected) : createProviderDraft());
    setFormError(''); setLogoFile(null); setVendorRevision((value) => value + 1);
    setErrors({});
    setModels([]);
    setTest({ status: 'idle' });
    setRevealApiKey(false);
    setMode('form');
  };

  const openEdit = (provider: ProviderConfig): void => {
    setDirection('forward');
    setEditingId(provider.id);
    const next = providerConfigToDraft(provider, readSecret(provider.apiKeyRef) ?? '');
    const currentVendor = vendors.find((item) => item.id === provider.vendorId);
    setDraft(currentVendor ? applyVendor(next, currentVendor, true) : next);
    setFormError(''); setLogoFile(null); setVendorRevision((value) => value + 1);
    setErrors({});
    setModels([]);
    setTest({ status: 'idle' });
    setRevealApiKey(false);
    setMode('form');
  };

  const closeForm = (): void => {
    if (saving) return;
    setDirection('backward');
    setMode('list');
  };

  const handleProtocolChange = (value: ProviderProtocol): void => {
    if (locks.protocol) return;
    if (value !== 'chat-completions' && locks.purpose && draft.purpose !== 'chat') return;
    setDraft((prev) => ({ ...prev, protocol: value, kind: protocolKind(value), purpose: value === 'chat-completions' ? prev.purpose : 'chat' }));
    setModels([]);
    setTest({ status: 'idle' });
  };

  const currentDraft = async (): Promise<ProviderDraft> => {
    if (!draft.vendorId) return draft;
    const rows = await fetchProviderVendors();
    setVendors(rows); setVendorStatus('ready');
    const current = rows.find((item) => item.id === draft.vendorId);
    if (!current) throw new Error('该模型厂商已删除，请返回列表后新建提供商');
    const next = applyVendor(draft, current, true);
    setDraft(next);
    return next;
  };

  const handleTest = async (): Promise<void> => {
    let checked: ProviderDraft;
    try { checked = await currentDraft(); }
    catch { setFormError('无法读取模型厂商规则，请刷新厂商列表后重试'); return; }
    const draftErrors = validateProviderDraft(checked);
    setErrors(draftErrors);
    if (hasDraftErrors(draftErrors)) {
      setTest({
        status: 'error',
        error: { code: 'config', message: '请先修正表单中的错误，再测试连接' },
      });
      return;
    }

    setTest({ status: 'testing' });
    const config = draftToProviderConfig(checked, editingId ?? 'draft-provider');
    const resolved: ResolvedProviderConfig = {
      ...config,
      ...(checked.apiKey.trim() === '' ? {} : { apiKey: checked.apiKey }),
    };
    const result = await testProviderConnection(resolved);
    if (result.ok) {
      setModels(result.models);
      setTest({ status: 'success', models: result.models });
    } else {
      setTest({ status: 'error', error: result.error });
    }
  };

  const handleSave = async (): Promise<void> => {
    if (saving) return;
    setSaving(true); setFormError('');
    try {
    const checked = await currentDraft();
    const draftErrors = validateProviderDraft(checked);
    setErrors(draftErrors);
    if (hasDraftErrors(draftErrors)) return;

    const id = editingId ?? newId();
    const config = draftToProviderConfig(checked, id);
    await putProvider(config);
    if (checked.apiKey.trim() === '') removeSecret(config.apiKeyRef);
    else writeSecret(config.apiKeyRef, checked.apiKey);

    await refresh();
    setDirection('backward'); setMode('list');
    } catch (failure) { setFormError(failure instanceof Error ? failure.message : '保存失败，请重试'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (provider: ProviderConfig): Promise<void> => {
    await updateProvider(provider.id, { enabled: !provider.enabled });
    await refresh();
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (deleteTarget === null) return;
    await deleteProvider(deleteTarget.id);
    removeSecret(deleteTarget.apiKeyRef);
    setDeleteTarget(null);
    await refresh();
  };

  const vendorIds = new Set(vendors.map((item) => item.id));
  const listRows = [
    ...vendors.flatMap<ProviderListRow>((item) => {
      const configured = providers.filter((provider) => provider.vendorId === item.id);
      return configured.length > 0
        ? configured.map((provider) => ({ key: provider.id, provider, vendor: item }))
        : [{ key: `vendor-${item.id}`, provider: null, vendor: item }];
    }),
    ...providers.filter((provider) => !provider.vendorId || !vendorIds.has(provider.vendorId))
      .map((provider) => ({ key: provider.id, provider, vendor: null })),
  ];

  const renderList = (): ReactElement => (
    <>
      <RegBlock meta={m('page.providers.root')} className={styles.page} ariaHidden surface="page" />
      <RegBlock meta={m('providers.header')} className={styles.header} ariaHidden />
      <RegHeading meta={m('providers.header.title')} className={styles.title}>
        模型提供商
      </RegHeading>
      <RegButton meta={m('providers.header.addButton')} className={styles.addButton} onClick={() => openCreate()}>
        ＋
      </RegButton>
      {vendorStatus !== 'ready' && <RegBlock meta={m('providers.catalogStatus')} className={styles.listStatus} ariaRole={vendorStatus === 'error' ? 'alert' : 'status'}>
        {vendorStatus === 'loading' ? '正在加载模型厂商…' : <>厂商列表加载失败 <button type="button" onClick={() => setVendorRevision((value) => value + 1)}>重试</button></>}
      </RegBlock>}
      {listRows.length === 0 && vendorStatus === 'ready' ? (
        <>
          <RegBlock meta={m('providers.empty')} className={styles.empty} ariaHidden />
          <RegText meta={m('providers.empty.title')} className={styles.emptyTitle}>
            {loaded ? '还没有提供商配置' : '正在加载…'}
          </RegText>
          <RegText meta={m('providers.empty.hint')} className={styles.emptyHint}>
            点击右上角 ＋ 添加 OpenAI / Anthropic / Gemini / 自定义
          </RegText>
        </>
      ) : (
        <RegList meta={m('providers.list')} className={vendorStatus === 'ready' ? styles.list : `${styles.list} ${styles.listWithStatus}`}>
          <div style={{ height: listRows.length * PROVIDER_ITEM_HEIGHT }} aria-hidden="true" />
          {listRows.map(({ key, provider, vendor: item }, index) => provider ? (
            <ProviderRow
              key={key}
              provider={provider}
              index={index}
              onToggle={(target) => {
                void handleToggle(target);
              }}
              onEdit={openEdit}
              onDelete={(target) => {
                setDeleteTarget(target);
              }}
            />
          ) : item && (
            <RegButton key={key} meta={{ id: `providers.vendor.${item.id}`, page: PAGE_ID, role: 'button',
              label: `配置厂商：${item.name}`, layout: providerItemLayout(index), zIndex: 4,
              props: ['name', 'protocol', 'purpose', 'baseUrl', 'logo'], notes: '后台厂商列表项；点击复用提供商配置页，安卓用 RecyclerView item' }}
              parent={PROVIDER_LIST_LAYOUT} className={styles.vendorItem} onClick={() => openCreate(item)}>
              {item.logo ? <img className={styles.vendorLogo} src={item.logo} alt="" referrerPolicy="no-referrer" /> : <span className={styles.vendorLogo}>{item.name.slice(0, 1)}</span>}
              <span className={styles.vendorDetails}>
                <span className={styles.vendorName}>{item.name}</span>
                <span className={styles.vendorMeta}>{PROTOCOL_LABELS[item.protocol]} · {PROVIDER_PURPOSE_LABEL[item.purpose]} · 未配置</span>
                <span className={styles.vendorMeta}>{item.baseUrl}</span>
              </span>
              <AppIcon name="chevron-right" size={18} />
            </RegButton>
          ))}
        </RegList>
      )}
    </>
  );

  const renderDeleteDialog = (): ReactElement | null => {
    if (deleteTarget === null) return null;
    return (
      <>
        <RegBlock meta={m('providers.deleteBackdrop')} className={styles.backdrop} ariaHidden />
        <RegBlock
          meta={m('providers.deleteDialog')}
          className={styles.dialog}
          ariaRole="dialog"
          ariaModal
        />
        <RegText meta={m('providers.deleteDialog.title')} className={styles.dialogTitle}>
          删除提供商？
        </RegText>
        <RegText meta={m('providers.deleteDialog.text')} className={styles.dialogText}>
          {`将删除「${deleteTarget.name}」及其本地密钥，操作不可恢复。`}
        </RegText>
        <RegButton
          meta={m('providers.deleteDialog.cancelButton')}
          className={styles.dialogSecondary}
          onClick={() => {
            setDeleteTarget(null);
          }}
        >
          取消
        </RegButton>
        <RegButton
          meta={m('providers.deleteDialog.confirmButton')}
          className={styles.dialogPrimary}
          onClick={() => {
            void handleConfirmDelete();
          }}
        >
          删除
        </RegButton>
      </>
    );
  };

  const renderForm = (): ReactElement => (
    <>
      <RegBlock meta={m('page.providers.form')} className={styles.form} ariaHidden surface="page" />
      <RegBlock meta={m('providers.form.header')} className={styles.header} ariaHidden />
      <RegButton meta={m('providers.form.backButton')} className={styles.backButton} onClick={closeForm}>
        ←
      </RegButton>
      <RegHeading meta={m('providers.form.title')} className={styles.formTitle}>
        {editingId === null ? '新增提供商' : '编辑提供商'}
      </RegHeading>
      <fieldset className={styles.formBody} disabled={saving} aria-busy={saving}>
      {draft.vendorId && vendorStatus === 'loading' && <p className={styles.catalogStatus} role="status">正在加载模型厂商…</p>}
      {draft.vendorId && vendorStatus === 'error' && <div className={styles.catalogStatus} role="alert">厂商列表加载失败 <button type="button" onClick={() => setVendorRevision((value) => value + 1)}>重试</button></div>}
      {draft.vendorId && vendorStatus === 'ready' && !vendor && <p className={styles.error} role="alert">该模型厂商已删除，请新建提供商</p>}

      <RegText meta={m('providers.form.logoLabel')} className={styles.label}>厂商 Logo{locks.logo ? '（已锁定）' : ''}</RegText>
      <div className={styles.logoRow}>
        {draft.logo ? <img className={styles.vendorLogo} src={draft.logo} alt="厂商 Logo" referrerPolicy="no-referrer" /> : <span className={styles.vendorLogo} aria-label="厂商图标">{draft.name.trim().slice(0, 1) || 'M'}</span>}
        <label className={styles.logoUpload} title="上传厂商 Logo" aria-disabled={locks.logo || saving}>
          <AppIcon name="upload" size={18} />
          <input type="file" aria-label="上传厂商 Logo" accept="image/png,image/jpeg,image/webp,image/gif" disabled={locks.logo || saving}
            onChange={(event) => { const file = event.currentTarget.files?.[0]; if (file) setLogoFile(file); event.currentTarget.value = ''; }} />
        </label>
        {draft.logo && <button type="button" title="清除 Logo" aria-label="清除厂商 Logo" disabled={locks.logo || saving} onClick={() => updateDraft({ logo: '' })}><AppIcon name="delete" size={18} /></button>}
      </div>
      <RegInput meta={m('providers.form.logoInput')} className={styles.input} value={draft.logo?.startsWith('data:') ? '' : draft.logo ?? ''} placeholder="https://example.com/logo.png" disabled={locks.logo || saving}
        onChange={(value) => updateDraft({ logo: value })} />
      {errors.logo && <p role="alert" className={styles.formError}>{errors.logo}</p>}

      <RegText meta={m('providers.form.kindLabel')} className={styles.label}>
        协议{locks.protocol ? '（已锁定）' : ''}
      </RegText>
      <RegSelect meta={m('providers.form.protocol')} className={styles.input} value={protocol} disabled={locks.protocol || saving}
        onChange={(value) => handleProtocolChange(value as ProviderProtocol)}>
        {(Object.keys(PROTOCOL_LABELS) as ProviderProtocol[]).map((value) => <option key={value} value={value} disabled={value !== 'chat-completions' && locks.purpose && draft.purpose !== 'chat'}>{PROTOCOL_LABELS[value]}</option>)}
      </RegSelect>
      <RegText meta={m('providers.form.kindHint')} className={styles.hint}>
        {`官方基址示例：${KIND_BASE_URL_HINT[draft.kind]}`}
      </RegText>

      <RegText meta={m('providers.form.purposeLabel')} className={styles.label}>
        模型类型{locks.purpose ? '（已锁定）' : ''}
      </RegText>
      <RegSelect
        meta={m('providers.form.purpose')}
        className={styles.input}
        value={draft.purpose}
        disabled={locks.purpose || saving}
        onChange={(value) => updateDraft({ purpose: value as 'chat' | 'image' | 'tts' })}
      >
        <option value="chat">{PROVIDER_PURPOSE_LABEL.chat}</option>
        <option value="image" disabled={protocol !== 'chat-completions' || !supportsImagePurpose(draft.kind)}>
          {PROVIDER_PURPOSE_LABEL.image}
        </option>
        <option value="tts" disabled={protocol !== 'chat-completions' || !supportsTtsPurpose(draft.kind)}>
          {PROVIDER_PURPOSE_LABEL.tts}
        </option>
      </RegSelect>
      {errors.purpose === undefined ? null : (
        <RegText meta={m('providers.form.purposeError')} className={styles.error}>
          {errors.purpose}
        </RegText>
      )}

      <RegText meta={m('providers.form.nameLabel')} className={styles.label}>
        厂商名{locks.name ? '（已锁定）' : ''}
      </RegText>
      <RegInput
        meta={m('providers.form.nameInput')}
        className={styles.input}
        value={draft.name}
        disabled={locks.name || saving}
        placeholder="例如：我的 Anthropic"
        autoComplete="off"
        onChange={(value) => updateDraft({ name: value })}
      />
      {errors.name === undefined ? null : (
        <RegText meta={m('providers.form.nameError')} className={styles.error}>
          {errors.name}
        </RegText>
      )}

      <RegText meta={m('providers.form.baseUrlLabel')} className={styles.label}>
        API 主机地址{locks.baseUrl ? '（已锁定）' : ''}
      </RegText>
      <RegInput
        meta={m('providers.form.baseUrlInput')}
        className={styles.input}
        value={draft.baseUrl}
        disabled={locks.baseUrl || saving}
        placeholder={KIND_BASE_URL_HINT[draft.kind]}
        autoComplete="off"
        onChange={(value) => updateDraft({ baseUrl: value })}
      />
      {errors.baseUrl === undefined ? null : (
        <RegText meta={m('providers.form.baseUrlError')} className={styles.error}>
          {errors.baseUrl}
        </RegText>
      )}

      <RegText meta={m('providers.form.apiKeyLabel')} className={styles.label}>
        API Key
      </RegText>
      <div className={styles.keyRow}>
      <RegInput
        meta={m('providers.form.apiKeyInput')}
        className={styles.input}
        type={revealApiKey ? 'text' : 'password'}
        value={draft.apiKey}
        placeholder="sk-…"
        autoComplete="off"
        onChange={(value) => updateDraft({ apiKey: value })}
      />
      <RegButton
        meta={m('providers.form.apiKeyRevealButton')}
        className={styles.revealButton}
        aria-pressed={revealApiKey}
        onClick={() => {
          setRevealApiKey((prev) => !prev);
        }}
      >
        {revealApiKey ? '隐藏' : '显形'}
      </RegButton>
      </div>

      <RegText meta={m('providers.form.defaultModelLabel')} className={styles.label}>
        默认模型
      </RegText>
      <RegInput
        meta={m('providers.form.defaultModelInput')}
        className={styles.input}
        value={draft.defaultModel}
        placeholder={models.length > 0 ? '从下拉选择或手工输入' : '测试连接后可选择'}
        listId={models.length > 0 ? MODEL_DATALIST_ID : undefined}
        autoComplete="off"
        onChange={(value) => updateDraft({ defaultModel: value })}
      />
      {models.length > 0 && (
        <datalist id={MODEL_DATALIST_ID}>
          {models.map((model) => (
            <option key={model} value={model} />
          ))}
        </datalist>
      )}

      <div className={styles.enabledRow}>
      <RegText meta={m('providers.form.enabledLabel')} className={styles.inlineLabel}>
        启用该提供商
      </RegText>
      <RegSwitch
        meta={m('providers.form.enabledToggle')}
        className={draft.enabled ? `${styles.toggle} ${styles.toggleOn}` : styles.toggle}
        checked={draft.enabled}
        onToggle={() => updateDraft({ enabled: !draft.enabled })}
      />
      </div>

      <RegButton
        meta={m('providers.form.testButton')}
        className={styles.testButton}
        disabled={test.status === 'testing' || saving || (!!draft.vendorId && (vendorStatus !== 'ready' || !vendor))}
        onClick={() => {
          void handleTest();
        }}
      >
        {test.status === 'testing' ? '测试中…' : '测试连接'}
      </RegButton>
      <RegText
        meta={m('providers.form.testResult')}
        className={test.status === 'error' ? `${styles.result} ${styles.resultError}` : styles.result}
      >
        {describeTestResult(test)}
      </RegText>
      {formError && <p role="alert" className={styles.formError}>{formError}</p>}
      <div className={styles.formActions}>
      <RegButton
        meta={m('providers.form.saveButton')}
        className={styles.saveButton}
        disabled={saving || (!!draft.vendorId && (vendorStatus !== 'ready' || !vendor))}
        onClick={() => {
          void handleSave();
        }}
      >
        {saving ? '保存中…' : '保存'}
      </RegButton>
      <RegButton meta={m('providers.form.cancelButton')} className={styles.cancelButton} disabled={saving} onClick={closeForm}>
        取消
      </RegButton>
      </div>
      </fieldset>
      {logoFile && <ImageCropDialog file={logoFile} kind="avatar" onCancel={() => setLogoFile(null)} onConfirm={(source) => {
        setLogoFile(null);
        void resizeVendorLogo(source).then((logo) => updateDraft({ logo })).catch(() => setFormError('Logo 处理失败，请重试'));
      }} />}
    </>
  );

  return (
    <AnimatePresence initial={false} custom={direction}>
      <motion.div
        key={mode}
        custom={direction}
        variants={presets.pageTransition}
        initial="enter"
        animate="center"
        exit="exit"
        style={{ position: 'absolute', inset: 0 }}
      >
        {mode === 'list' ? renderList() : renderForm()}
        {mode === 'list' ? renderDeleteDialog() : null}
      </motion.div>
    </AnimatePresence>
  );
}
